import type { Attachment, MessageCard } from "./types.js";

// A row's `text` as the renderers read it: one forged frame stored a number (QA run 11).
export function messageText(value: unknown): string {
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return "";
}

const TEXT_FRAMES = new Set([
  "cyborg:channel_message_broadcast",
  "cyborg:dm_broadcast",
  "cyborg:edit_message_broadcast",
]);

// The string ids of a row's `mentions`, as the relay stores them: a forged `12345` threw on render.
export function messageMentions(value: unknown): string[] | null {
  if (!Array.isArray(value)) return null; // null: no mention list
  return value.filter((id): id is string => typeof id === "string");
}

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);

// MessageAttachments reads `type.startsWith` and `name`; ChatMessage switches on a card's `kind`.
// Any object is an attachment, as on trunk; a missing type or name becomes "" so a renderer never throws.
const asAttachment = (a: Record<string, unknown>): Attachment => ({
  ...(a as unknown as Attachment), // cast: only type and name are read unguarded; both are coerced below
  type: typeof a.type === "string" ? a.type : "",
  name: typeof a.name === "string" ? a.name : "",
});

export function messageAttachments(value: unknown): Attachment[] | null {
  if (!Array.isArray(value)) return null; // null: no attachments
  const kept = value.filter(isRecord).map(asAttachment);
  return kept.length === 0 && value.length > 0 ? null : kept; // null: every entry was malformed; [] stays []
}

// The renderers branch on `kind` and read each kind's fields defensively.
const isCard = (c: unknown): c is MessageCard => isRecord(c) && typeof c.kind === "string";

export function messageCard(value: unknown): MessageCard | null {
  return isCard(value) ? value : null; // null: absent or forged
}

function fixRow(row: unknown, isMessage: boolean): void {
  if (!isRecord(row)) return;
  if (!isMessage && !("fromId" in row)) return;
  if ("text" in row && typeof row.text !== "string") row.text = messageText(row.text);
  if ("mentions" in row && row.mentions !== null) row.mentions = messageMentions(row.mentions);
  if ("attachments" in row && row.attachments != null)
    row.attachments = messageAttachments(row.attachments);
  if ("card" in row && row.card != null) row.card = messageCard(row.card);
}

// Called once per inbound frame (client.handleMessage), before anything reads its message rows.
export function normalizeFrameMessageText(
  type: string,
  payload: Record<string, unknown> | undefined,
): void {
  if (!payload) return;
  if (TEXT_FRAMES.has(type)) fixRow(payload, true);
  for (const key of ["messages", "upserts"]) {
    const rows = payload[key];
    if (Array.isArray(rows)) for (const row of rows) fixRow(row, false);
  }
  fixRow(payload.message, false);
  fixRow(payload.root, false);
  const threads = Array.isArray(payload.threads) ? payload.threads : [];
  for (const thread of threads) if (isRecord(thread)) fixRow(thread.root, false);
}
